import { PRProduct } from '@/domains/core/tracking/interfaces/PRProduct';
import type {
  DetailMdScore,
  MdScore,
} from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { type Brand } from '@/domains/productDiscovery/ProductPage/modules/Brand/brand.model';
import { mapProductDetailToPRProduct } from '@/domains/productDiscovery/ProductPage/utils/gtm/mapProductDetailToPRProduct';
import type { CategoryItem } from '@/domains/productDiscovery/Recommendation/interfaces/msCatalogCategory';
import { CarbonScoreEquivalence } from '@/domains/sustainability/carbonScore/data/carbonScore.model';
import { getTags } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';

import type { Coupon } from '../../interfaces/Detail.common.model';
import type { MarketDetail } from '../../interfaces/Detail.model';

const round2Decimals = (num: number) =>
  Math.round((num + Number.EPSILON) * 100) / 100;

interface Params {
  brand: Brand;
  detail: MarketDetail;
  coupons: Coupon[];
  isSponsoredProductAvailable: boolean;
  isBulkAvailable: boolean;
  mdScores?: MdScore[];
}

// TODO: refactor this file while full implement (without 'TODO' is done)
// TODO: add missing tests

export type GTMViewProductPageEvent = {
  event: 'interaction_product detail';
  e_category: 'ecommerce';
  e_action: 'product detail';
  e_label: string | undefined;
  interaction_hit: true;
  event_trigger: 'product_page';
  carbon_score?: CarbonScoreEquivalence | undefined;
  hasVideo: boolean;
  mabaya_trigger: false;
  variant_legacy_design: boolean | 'none';
  policy_return_count?: number;
  policy_return_is_free?: boolean;
  has_md_ranking_playlist: boolean | undefined;
  has_useful_reviews: boolean;
  is_bulk_available: boolean;
  pr_products: Array<PRProduct>;
  listing_fingerprint?: undefined;
  isSpAvailable: boolean;
  picture_count: number;
};

export const GTM_VIEW_PRODUCT_PAGE = ({
  brand,
  detail,
  coupons,
  isSponsoredProductAvailable,
  isBulkAvailable,
  mdScores,
}: Params): GTMViewProductPageEvent => {
  const withPromo =
    getTags(
      detail.price?.primaryPrice.amountWithVat ?? 0,
      detail.price?.retailPrice?.amountWithVat ?? 0,
      detail.commercialAnimation?.tags,
    ).length > 0;

  const variantLegacyDesign =
    detail.totalModels === 1
      ? 'none'
      : Boolean(detail.totalModels > 1 && !detail.variants);
  const b2bPriceAdvantageAmount =
    typeof detail.price?.proAmountSavedWithVat === 'number' &&
    (detail.price?.primaryPrice.amountWithVat ?? 0) > 0
      ? round2Decimals(
          (detail.price.proAmountSavedWithVat /
            (detail.price.primaryPrice.amountWithVat ?? 0)) *
            100,
        )
      : 0;

  return {
    event: 'interaction_product detail',
    e_category: 'ecommerce',
    e_action: 'product detail',
    e_label: detail.title,
    interaction_hit: true,
    event_trigger: 'product_page',
    carbon_score: detail?.carbonScore?.product.carbonScore,
    hasVideo: detail?.media.some((item) =>
      ['youtube', 'dailymotion', 'vimeo'].includes(item.type || ''),
    ),
    mabaya_trigger: false,
    variant_legacy_design: variantLegacyDesign,
    policy_return_count: detail.sellerReturnPolicy?.returnWithdrawalLimit,
    policy_return_is_free: detail.sellerReturnPolicy?.returnFeesFree,
    has_md_ranking_playlist: detail.mdScores?.some(
      (md: DetailMdScore) => md.ranking.length >= 5,
    ),
    has_useful_reviews:
      detail.latestReviews?.bestRatedReviews?.some(
        (review) => review.votes > 0,
      ) || false,
    is_bulk_available: isBulkAvailable,
    pr_products: [
      mapProductDetailToPRProduct(
        detail,
        brand,
        {
          pr_b2b_price_advantage_count:
            !!detail.price?.proAmountSavedWithVat && !withPromo
              ? b2bPriceAdvantageAmount
              : undefined,
          pr_has_a_coupon: !!coupons.length,
          pr_is_in_promo: withPromo,
          pr_md_count:
            mdScores?.filter((mdr) => !!mdr.nbTotal && !!mdr.avgRating)
              .length || 0,
          pr_shipping_options: [],
          pr_shipping_options_available_count: 0,
          pr_shipping_options_count: 0,
        },
        mdScores,
      ),
    ],
    listing_fingerprint: undefined,
    isSpAvailable: isSponsoredProductAvailable,
    picture_count: detail.media.length,
  };
};

export const GTM_CLICK_ON_SEE_MORE_QNA = () => ({
  event: 'interaction_click on see more q&a',
  e_category: 'navigation',
  e_action: 'Click on see more q&a',
  e_label: undefined,
  event_trigger: 'product page',
  amp_event_name: 'Click on see more q&a',
  interaction_hit: false,
});

export const GTM_CLICK_ON_A2C_MODAL_COMPLEMENTARY_PRODUCTS = (
  { name, id }: Omit<CategoryItem, 'parent' | 'parents' | 'platform'>,
  link: string,
  position: number,
  playlistLocation: string,
) => ({
  event: 'interaction_click on a category',
  e_category: 'navigation',
  event_trigger: playlistLocation,
  e_action: 'Click on a category',
  e_label: name,
  amp_event_name: 'Click on a category',
  redirection_link: link,
  category_name: name,
  category_id: id,
  category_position: position,
  interaction_hit: false,
});

export const GTM_DISPLAY_CATEGORY_COUPLES_A2C_POPIN = (
  items: Omit<CategoryItem, 'parent' | 'parents' | 'platform'>[] = [],
  currency: 'EUR' | 'GBP',
) => {
  const ids = items.map(({ id }) => id);
  const names = items.map(({ name }) => name);

  return {
    event: 'interaction_display playlist',
    e_category: 'navigation',
    e_action: 'Display playlist',
    e_label: 'CategoryCouples',
    amp_event_name: 'Display playlist',
    event_trigger: 'A2C_popin',
    currency,
    block_clicked: undefined,
    listing_fingerprint: undefined,
    playlist_article_ids: undefined,
    playlist_model_ids: undefined,
    playlist_category_ids: ids,
    playlist_product_ids: undefined,
    playlist_b2b_product_count: undefined,
    playlist_content_id: undefined,
    playlist_fingerprint: undefined,
    playlist_reconciliation: 'CategoryCouples_A2CPopin',
    playlist_position: undefined,
    category_name: names,
    category_id: ids,
    interaction_hit: false,
    pr_products: [
      {
        list_name: undefined,
        order_id: undefined,
        order_ref: undefined,
        pr_id: undefined,
        pr_product_id: undefined,
        pr_name: undefined,
        pr_stock: undefined,
        pr_price: undefined,
        pr_base_price: undefined,
        pr_degraded_detail: undefined,
        pr_is_degraded: undefined,
        pr_lowest_category_name: undefined,
        pr_lowest_category_id: undefined,
        pr_family_name: undefined,
        pr_family_id: undefined,
        pr_category_name: undefined,
        pr_category_id: undefined,
        pr_subcategory_name: undefined,
        pr_subcategory_id: undefined,
        pr_subsubcategory_name: undefined,
        pr_subsubcategory_id: undefined,
        pr_seller_name: undefined,
        pr_seller_id: undefined,
        pr_brand_name: undefined,
        pr_brand_id: undefined,
        pr_model_name: undefined,
        pr_model_id: undefined,
        pr_variant: undefined,
        pr_variant_count: undefined,
        pr_rating: undefined,
        pr_rating_count: undefined,
        pr_quantity: undefined,
        pr_attributes_count: undefined,
        pr_is_restocking: undefined,
        pr_is_mf: undefined,
        pr_has_free_delivery: undefined,
        pr_has_24hours_shipping: undefined,
        pr_has_48hours_shipping: undefined,
        pr_has_pickuppoint_shipping: undefined,
        pr_has_home_shipping: undefined,
        pr_shipping_options_count: undefined,
        pr_is_top_sales: undefined,
        pr_is_pro: undefined,
        pr_is_sponsored: undefined,
        search_query: undefined,
        reco_engine_name: undefined,
      },
    ],
  };
};

export const GTM_CLICK_ON_LEVIA = () => ({
  event: 'interaction_click on levia widget',
  e_category: 'navigation',
  e_action: 'Click on levia widget',
  event_trigger: 'product_page',
  amp_event_name: 'Click on levia widget',
  interaction_hit: false,
});
